import {
  Observable,
  catchError,
  combineLatest,
  map,
  of,
  switchMap,
} from "rxjs";
import { DatabaseFieldType } from "~/classes/database/database_field_type.enum";
import { DBFactory } from "~/classes/database/db_factory";
import { Assignment, Assignments } from "../assignments/assignment.model";
import {
  StudentLinkedAccount,
  StudentLinkedAccounts,
} from "../studentLinkedAccounts/student-linked-account.model";
import { ClassroomLinkedUser } from "./classroom-linked-user.model";
import { Classroom, Classrooms } from "./classroom.model";

export class ClassroomsService {
  static async get(id: string) {
    const db = DBFactory.createDatabase();

    const databaseData = await db.get({
      collection: "classrooms",
      path: `classrooms/${id}`,
    });

    return Classroom.fromMap(databaseData);
  }

  static async listAllClassrooms() {
    const db = DBFactory.createDatabase();

    const databaseData = await db.list({
      collection: `/classrooms`,
      path: `/classrooms`,
    });

    return databaseData.map((data) => {
      return Classroom.fromMap(data);
    });
  }

  static async listClassroomsToSync() {
    const limit = process.env.CLASSROOMS_SYNC_LIMIT
      ? parseInt(process.env.CLASSROOMS_SYNC_LIMIT)
      : 100;

    const db = DBFactory.createDatabase();

    const databaseData = await db.list(
      {
        collection: `/classrooms`,
        path: `/classrooms`,
      },
      [
        {
          type: "where",
          field: "externalProvider",
          operator: "in",
          value: ["sakai"],
        },
        {
          type: "where",
          field: "lastSyncedTimestamp",
          operator: "<",
          // Get's classrooms whose assignemnts haven't been synced in 10 hours
          value: Date.now() - 1000 * 60 * 60 * 10,
        },
        {
          type: "orderBy",
          field: "lastSyncedTimestamp",
          direction: "asc",
        },
        {
          type: "limit",
          value: limit,
        },
      ]
    );

    const classrooms = databaseData
      .map((data) => {
        return Classroom.fromMap(data);
      })
      .filter((classroom) => classroom.isArchived != true);

    // if (classrooms.length >= limit) return classrooms;

    return classrooms;

    // const allClassrooms = await this.listAllClassrooms();
    // const classroomsWithoutTimestamps = allClassrooms.filter((classroom) => {
    //   return (
    //     classroom.isArchived != true &&
    //     !classroom.lastSyncedTimestamp &&
    //     ["sakai", "google", "canvas"].includes(classroom.externalProvider)
    //   );
    // });

    // // Get the first 10 lmsUserConfigs that don't have a timestamp
    // const classroomsWithoutTimestampsLimited =
    //   classroomsWithoutTimestamps.slice(0, limit - classrooms.length);

    // return [...classrooms, ...classroomsWithoutTimestampsLimited];
  }

  static async listAllOpenAssments(): Promise<Assignments> {
    const db = DBFactory.createDatabase();

    const openStatuses = ["DUE", "OPEN"]; // I'll change it to ["OPEN", "DUE"] after I think

    const databaseData = await db.list(
      {
        collection: `/assignments`,
        path: `/assignments`,
      },
      [
        {
          type: "where",
          field: "status",
          operator: "in",
          value: openStatuses,
        },
      ]
    );

    return databaseData.map((data) => {
      return Assignment.fromMap(data);
    });
  }

  static async listAssignmentsToSync(
    ignoreLimit: boolean = false
  ): Promise<Assignments> {
    let limit = process.env.ASSIGNMENT_SYNC_LIMIT
      ? parseInt(process.env.ASSIGNMENT_SYNC_LIMIT)
      : 200;

    if (ignoreLimit) {
      limit = 1000000000;
    }

    const db = DBFactory.createDatabase();

    const openStatuses = ["DUE", "OPEN"]; // I'll change it to ["OPEN", "DUE"] after I think
    const lmsToAutoSync = ["sakai"];

    // we are no longer syncing assignments from google classroom automatically
    const queryData = [
      {
        type: "where",
        field: "status",
        operator: "in",
        value: openStatuses,
      },
      {
        type: "where",
        field: "externalProvider",
        operator: "in",
        value: lmsToAutoSync,
      },
      {
        type: "orderBy",
        field: "lastSyncedTimestamp",
        direction: "asc",
      },
    ] as ModelQueryConfig;

    if (ignoreLimit != true) {
      queryData.push({
        type: "where",
        field: "lastSyncedTimestamp",
        operator: "<",
        // Get's classrooms whose assignemnts haven't been synced in 30 minutes
        value: Date.now() - 1000 * 60 * 30,
      });

      queryData.push({
        type: "limit",
        value: limit,
      });
    }

    const databaseData = await db.list(
      {
        collection: `/assignments`,
        path: `/assignments`,
      },
      queryData
    );

    const assignments = databaseData
      .map((data) => {
        return Assignment.fromMap(data);
      })
      .filter((assignment) => assignment.isArchived != true);

    return assignments;

    // const allAssignments = await this.listAllOpenAssments();
    // const assignmentsWithoutTimestamps = allAssignments.filter((assignment) => {
    //   return (
    //     !assignment.lastSyncedTimestamp &&
    //     lmsToAutoSync.includes(assignment.externalProvider) &&
    //     assignment.isArchived != true
    //   );
    // });

    // // Get the first 10 lmsUserConfigs that don't have a timestamp
    // const assignmentsWithoutTimestampsLimited =
    //   assignmentsWithoutTimestamps.slice(0, limit - assignments.length);

    // return [...assignments, ...assignmentsWithoutTimestampsLimited];
  }

  static async listUserClassrooms(
    userId: string,
    options?: {
      organizationIds?: string[];
    }
  ) {
    const db = DBFactory.createDatabase();

    const databaseData = await db.list(
      {
        collection: `/classrooms`,
        path: `/classrooms`,
      },
      this.buildUserClassroomQuery(userId, options)
    );

    return databaseData.map((data) => {
      try {
        Classroom.fromMap(data);
      } catch (error) {
        // empty
      }

      return Classroom.fromMap(data);
    });
  }

  static async getOrganizationClassrooms({
    organizationId,
  }: {
    organizationId: string;
  }) {
    const db = DBFactory.createDatabase();

    const data = await db.list(
      {
        collection: `/classrooms`,
        path: `/classrooms`,
      },
      [
        {
          type: "where",
          field: "organizationId",
          operator: "==",
          value: organizationId,
        },
      ]
    );

    return data.map((data) => {
      return Classroom.fromMap(data);
    });
  }

  static async getUserClassrooms(
    userId: string,
    options?: {
      organizationIds?: string[];
    }
  ) {
    const db = DBFactory.createDatabase();

    const data = await db.list(
      {
        collection: `/classrooms`,
        path: `/classrooms`,
      },
      this.buildUserClassroomQuery(userId, options)
    );

    return data.map((data) => {
      return Classroom.fromMap(data);
    });
  }

  static streamOrganizationClassrooms({
    organizationId,
  }: {
    organizationId: string;
  }) {
    const db = DBFactory.createDatabase();

    return db
      .streamList(
        {
          collection: `/classrooms`,
          path: `/classrooms`,
        },
        [
          {
            type: "where",
            field: "organizationId",
            operator: "==",
            value: organizationId,
          },
          {
            type: "orderBy",
            field: "name",
            direction: "asc",
          },
        ]
      )
      .pipe(
        map((data) => {
          return data.map((data) => {
            return Classroom.fromMap(data);
          });
        })
      );
  }

  static streamUserClassrooms(
    userId: string,
    options?: {
      organizationIds?: string[];
    }
  ) {
    const db = DBFactory.createDatabase();

    const userClassroomsStream = db
      .streamList(
        {
          collection: `/classrooms`,
          path: `/classrooms`,
        },
        this.buildUserClassroomQuery(userId, options)
      )
      .pipe(
        catchError((error: any) => {
          return [];
        }),
        map((databaseData) => {
          return databaseData.map((data) => Classroom.fromMap(data));
        })
      );

    if (options?.organizationIds && options.organizationIds.length > 0) {
      const organizationClassroomsStream = db
        .streamList(
          {
            collection: `/classrooms`,
            path: `/classrooms`,
          },
          [
            {
              type: "where",
              field: "organizationId",
              operator: "in",
              value: options.organizationIds,
            },
          ]
        )
        .pipe(
          map((databaseData) => {
            return databaseData.map((data) => Classroom.fromMap(data));
          }),
          catchError((error: any) => {
            return [];
          })
        );

      return combineLatest([
        userClassroomsStream,
        organizationClassroomsStream,
      ]).pipe(
        map(([userClassrooms, organizationClassrooms]) => {
          const classrooms = [
            ...userClassrooms,
            ...organizationClassrooms,
          ].sort((a, b) => a.name.localeCompare(b.name));

          // Remove duplicates
          return classrooms.filter(
            (classroom, index, self) =>
              index === self.findIndex((t) => t.id === classroom.id)
          );
        }),
        catchError((error: any) => {
          return [];
        })
      );
    }

    return userClassroomsStream;
  }

  static buildUserClassroomQuery(
    userId: string,
    options?: {
      organizationIds?: string[];
    }
  ) {
    const queryData = [
      {
        type: "where",
        field: "userId",
        operator: "==",
        value: userId,
      },
    ] as ModelQueryConfig;

    queryData.push({
      type: "orderBy",
      field: "name",
      direction: "asc",
    });

    return queryData;
  }

  static async saveClassrooms(classrooms: Classrooms) {
    const db = DBFactory.createDatabase();

    await db.batchUpdate(classrooms);
  }

  static async deleteClassrooms(classrooms: Classrooms) {
    const db = DBFactory.createDatabase();

    await db.batchDelete(classrooms);
  }

  static getLMSUserConfig = async (userId: string) => {
    const db = DBFactory.createDatabase();

    const lmsUserConfig = await db.get({
      collection: "lmsUserConfigs",
      path: `lmsUserConfigs/${userId}`,
    });

    return lmsUserConfig as LMSUserConfig | undefined;
  };

  static streamAllStudentLinkedAccounts({
    classroomId,
  }: {
    classroomId: string;
  }): Observable<StudentLinkedAccounts> {
    const db = DBFactory.createDatabase();

    return db
      .streamList(
        {
          collectionGroup: "linkedAccounts",
          collection: "linkedAccounts",
          path: `linkedAccounts`,
        },
        [
          {
            type: "where",
            field: "classroomId",
            operator: "==",
            value: classroomId,
          },
          {
            type: "where",
            field: "isArchived",
            operator: "==",
            value: false,
          },
        ]
      )
      .pipe(
        map((databaseData) => {
          return databaseData.map((data) => {
            return StudentLinkedAccount.fromMap(data);
          });
        })
      );
  }

  static async getClassroomLinkedUsers(classroomId: string) {
    const db = DBFactory.createDatabase();

    const databaseData = await db.list({
      collection: `/classrooms/${classroomId}/linkedUsers`,
      path: `/classrooms/${classroomId}/linkedUsers`,
    });

    return databaseData.map((data) => {
      return ClassroomLinkedUser.fromMap(data);
    });
  }

  static streamClassroomLinkedUsers({ classroomId }: { classroomId: string }) {
    const db = DBFactory.createDatabase();

    return db
      .streamList({
        collection: `/classrooms/${classroomId}/linkedUsers`,
        path: `/classrooms/${classroomId}/linkedUsers`,
      })
      .pipe(
        map((databaseData) => {
          return databaseData.map((data) => {
            return ClassroomLinkedUser.fromMap(data);
          });
        })
      );
  }

  static async getUserClassroomPermissions({ userId }: { userId: string }) {
    try {
      const db = DBFactory.createDatabase();

      const databaseData = await db.get({
        collection: "/userClassroomPermissions",
        path: `/userClassroomPermissions/${userId}`,
      });

      return databaseData as {
        linkedClassroomIds: string[];
        teacherClassroomIds: string[];
      };
    } catch (error) {
      console.log("Error getting user classroom permissions", error);
      return {
        linkedClassroomIds: [],
        teacherClassroomIds: [],
      };
    }
  }

  static async saveUserClassroomPermissions({
    userId,
    userClassroomPermissions,
  }: {
    userId: string;
    userClassroomPermissions: {
      linkedClassroomIds: string[];
      teacherClassroomIds: string[];
    };
  }) {
    const db = DBFactory.createDatabase();

    await db.save(userClassroomPermissions, {
      collection: "/userClassroomPermissions",
      path: `/userClassroomPermissions/${userId}`,
    });
  }

  static streamUserLinkedClassrooms({ userId }: { userId: string }) {
    const db = DBFactory.createDatabase();

    return db
      .streamRecord({
        collection: "/userClassroomPermissions",
        path: `/userClassroomPermissions/${userId}`,
      })
      .pipe(
        catchError((error) => {
          return of({} as any);
        }),
        switchMap((data) => {
          if (data == undefined) {
            return of([]);
          }

          const linkedClassroomIds = data.linkedClassroomIds ?? [];

          if (linkedClassroomIds.length == 0) {
            return of([]);
          }

          const chunkSize = 10;
          const classroomIdChunks = [];
          for (let i = 0; i < linkedClassroomIds.length; i += chunkSize) {
            classroomIdChunks.push(linkedClassroomIds.slice(i, i + chunkSize));
          }

          const observables = classroomIdChunks.map((chunk) => {
            return db
              .streamList(
                {
                  collection: "/classrooms",
                  path: "classrooms",
                },
                [
                  {
                    type: "where",
                    field: "id",
                    operator: "in",
                    fieldType: DatabaseFieldType.id,
                    value: chunk,
                  },
                ]
              )
              .pipe(
                catchError((error) => {
                  return of([]);
                })
              );
          });

          return combineLatest(observables).pipe(
            catchError((error) => {
              return of([]);
            }),
            map((data) => {
              const classroomsData = data.flat();

              return classroomsData.map((data) => {
                return Classroom.fromMap(data);
              });
            })
          );
        })
      );
  }
}
